<template>
  <v-app class="home-inventory-app">
    <!-- Loading Overlay -->
    <v-overlay v-model="loader" :z-index="9999" opacity="0.8">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>

    <v-main class="auth-pages">
      <!-- Header Section -->
      <div class="inventory-header">
        <v-container class="py-4">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="6" class="text-left">
              <v-btn color="#3A7D7C" dark @click="$router.push('/')">
                <i class="material-icons-round mr-2">arrow_back</i>
                Back
              </v-btn>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center justify-space-between">
              <div>
                <h1 class="header-title">Available Homes</h1>
                <p class="header-subtitle">
                  {{ homes.length }} properties available
                  <span v-if="preApprovalAmount" class="ml-2">
                    • Pre-approved up to ${{ formatPrice(preApprovalAmount) }}
                  </span>
                </p>
              </div>
              <v-btn
                icon
                color="#3A7D7C"
                @click="shareLink"
                class="ml-4"
              >
                <i class="material-icons-round">share</i>
              </v-btn>
            </v-col>
          </v-row>

          <!-- Pre-Approval Bar -->
          <v-row class="mt-4" v-if="!preApprovalAmount">
            <v-col cols="12">
              <v-alert
                color="#3A7D7C"
                dark
                prominent
                class="pre-approval-alert"
              >
                <v-row align="center">
                  <v-col cols="12" md="8">
                    <div class="d-flex align-center">
                      <i class="material-icons-round mr-3" style="font-size: 40px;">verified_user</i>
                      <div>
                        <h3 class="mb-1">Get Pre-Approved in Minutes</h3>
                        <p class="mb-0">See which homes you qualify for and make your search easier</p>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4" class="text-right">
                    <v-btn
                      x-large
                      color="white"
                      class="pre-approval-btn"
                      @click="openPreApprovalModal"
                    >
                      <i class="material-icons-round mr-2">calculate</i>
                      Get Pre-Approved
                    </v-btn>
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Search and Filter Bar -->
          <v-row class="mt-4">
            <v-col cols="12" md="8">
              <v-text-field
                v-model="searchTerm"
                placeholder="Search by location, type, or features..."
                outlined
                dense
                hide-details
                class="search-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="sortBy"
                :items="sortOptions"
                outlined
                dense
                hide-details
                class="sort-select"
              ></v-select>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Pre-Approval Modal -->
      <v-dialog v-model="preApprovalDialog" max-width="600" persistent>
        <v-card class="pre-approval-modal">
          <v-card-title class="modal-header">
            <i class="material-icons-round mr-2">calculate</i>
            Get Pre-Approved
            <v-spacer></v-spacer>
            <v-btn icon @click="closePreApprovalModal">
              <i class="material-icons-round">close</i>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-6">
            <p class="modal-subtitle mb-6">
              Answer a few quick questions to see how much home you can afford
            </p>

            <!-- Credit Score Range -->
            <div class="form-group mb-6">
              <label class="form-label">Credit Score Range</label>
              <v-select
                v-model="preApprovalForm.creditScore"
                :items="creditScoreOptions"
                outlined
                dense
                placeholder="Select your credit score range"
              ></v-select>
            </div>

            <!-- Gross Monthly Income -->
            <div class="form-group mb-6">
              <label class="form-label">Gross Monthly Income</label>
              <v-text-field
                v-model="preApprovalForm.monthlyIncome"
                outlined
                dense
                prefix="$"
                type="number"
                placeholder="5,000"
              ></v-text-field>
            </div>

            <!-- Debt Toggle -->
            <div class="form-group mb-4">
              <v-switch
                v-model="showDebtField"
                color="#3A7D7C"
                label="I have monthly debt payments"
                class="mt-0"
              ></v-switch>
            </div>

            <!-- Monthly Debt (Conditional) -->
            <div v-if="showDebtField" class="form-group mb-6">
              <label class="form-label">Monthly Debt Payments</label>
              <v-text-field
                v-model="preApprovalForm.monthlyDebt"
                outlined
                dense
                prefix="$"
                type="number"
                placeholder="500"
                hint="Include car loans, credit cards, student loans, etc."
                persistent-hint
              ></v-text-field>
            </div>

            <!-- Down Payment Section -->
            <div class="form-group mb-6">
              <label class="form-label">Down Payment</label>
              <v-radio-group
                v-model="downPaymentType"
                row
                class="mt-2 mb-3"
              >
                <v-radio
                  label="Percentage"
                  value="percentage"
                  color="#3A7D7C"
                ></v-radio>
                <v-radio
                  label="Fixed Amount"
                  value="fixed"
                  color="#3A7D7C"
                ></v-radio>
              </v-radio-group>

              <!-- Percentage Input -->
              <v-text-field
                v-if="downPaymentType === 'percentage'"
                v-model="preApprovalForm.downPaymentPercent"
                outlined
                dense
                suffix="%"
                type="number"
                placeholder="20"
                hint="Typical down payment is 20%"
                persistent-hint
                min="0"
                max="100"
              ></v-text-field>

              <!-- Fixed Amount Input -->
              <v-text-field
                v-if="downPaymentType === 'fixed'"
                v-model="preApprovalForm.downPaymentFixed"
                outlined
                dense
                prefix="$"
                type="number"
                placeholder="50,000"
                hint="Enter your available down payment amount"
                persistent-hint
                min="0"
              ></v-text-field>
            </div>

            <!-- Calculate Button -->
            <v-btn
              block
              x-large
              color="#3A7D7C"
              dark
              class="calculate-btn"
              @click="calculatePreApproval"
              :loading="calculating"
              :disabled="!isFormValid"
            >
              <i class="material-icons-round mr-2">check_circle</i>
              Calculate My Pre-Approval
            </v-btn>

            <!-- Results Section -->
            <v-expand-transition>
              <div v-if="preApprovalAmount" class="results-section mt-6">
                <v-alert
                  color="#4CAF50"
                  dark
                  prominent
                  class="results-alert"
                >
                  <div class="text-center">
                    <i class="material-icons-round mb-2" style="font-size: 48px;">check_circle</i>
                    <h2 class="mb-2">Congratulations!</h2>
                    <h3 class="mb-3">You're pre-approved for up to</h3>
                    <div class="approval-amount">${{ formatPrice(preApprovalAmount) }}</div>

                    <!-- Down Payment Info -->
                    <div class="mt-3 mb-2" style="font-size: 14px; opacity: 0.9;">
                      <span v-if="downPaymentType === 'percentage'">
                        With {{ preApprovalForm.downPaymentPercent }}% down payment
                        (${{ formatPrice(preApprovalAmount * (preApprovalForm.downPaymentPercent / 100)) }})
                      </span>
                      <span v-else>
                        With ${{ formatPrice(preApprovalForm.downPaymentFixed) }} down payment
                      </span>
                    </div>

                    <p class="mt-2 mb-0">
                      {{ qualifiedHomesCount }} homes match your budget
                    </p>
                  </div>
                </v-alert>

                <v-btn
                  block
                  large
                  color="#3A7D7C"
                  dark
                  class="mt-4"
                  @click="applyPreApprovalFilter"
                >
                  <i class="material-icons-round mr-2">filter_list</i>
                  Show Qualified Homes
                </v-btn>
              </div>
            </v-expand-transition>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Agent Connection Dialog -->
      <v-dialog
        v-model="agentConnectionDialog"
        max-width="600"
        persistent
      >
        <v-card class="agent-connection-modal">
          <v-card-title class="modal-header">
            <div class="header-content">
              <span>Connect with Your Agent</span>
            </div>
            <v-btn icon @click="closeAgentDialog">
              <i class="material-icons-round">close</i>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-6" v-if="selectedHome">
            <!-- Selected Home Info -->
            <div class="selected-home-info mb-4">
              <v-img
                :src="selectedHome.image"
                height="200"
                class="rounded-lg mb-3"
              ></v-img>
              <h3 class="mb-2">{{ selectedHome.address }}</h3>
              <div class="home-details">
                <span class="price">${{ formatPrice(selectedHome.price) }}</span>
                <span>{{ selectedHome.beds }} bed</span>
                <span>{{ selectedHome.baths }} bath</span>
                <span>{{ formatPrice(selectedHome.sqft) }} sqft</span>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Step 1: Interest Message -->
            <div v-if="!showContactForm" class="connection-message text-center mb-4">
              <i class="material-icons-round mb-3" style="font-size: 48px; color: #3A7D7C;">person_pin_circle</i>
              <h3 class="mb-2">Interested in This Home?</h3>
              <p style="color: #6B8E9E; line-height: 1.6;">
                Connect with your agent to schedule a viewing, get more details, or discuss your pre-approval for this property.
              </p>

              <div v-if="preApprovalAmount" class="pre-approval-badge mt-3 mb-3">
                <i class="material-icons-round mr-2">verified</i>
                <span>You're pre-approved for up to ${{ formatPrice(preApprovalAmount) }}</span>
              </div>

              <!-- Initial Connect Button -->
              <v-btn
                block
                x-large
                color="#3A7D7C"
                dark
                class="connect-btn mb-3 mt-4"
                @click="showContactForm = true"
              >
                <i class="material-icons-round mr-2">phone</i>
                Yes, Connect Me with My Agent
              </v-btn>

              <v-btn
                block
                large
                outlined
                color="#3A7D7C"
                @click="closeAgentDialog"
              >
                <i class="material-icons-round mr-2">close</i>
                Maybe Later
              </v-btn>
            </div>

            <!-- Step 2: Contact Form -->
            <div v-if="showContactForm" class="contact-form-section">
              <div class="text-center mb-4">
                <i class="material-icons-round mb-2" style="font-size: 40px; color: #3A7D7C;">contact_mail</i>
                <h3 class="mb-2">Your Contact Information</h3>
                <p style="color: #6B8E9E;">
                  Please provide your details so the agent can reach you.
                </p>
              </div>

              <!-- Contact Form Fields -->
              <v-form ref="contactForm" v-model="contactFormValid">
                <v-text-field
                  v-model="contactForm.name"
                  label="Full Name"
                  outlined
                  dense
                  :rules="[v => !!v || 'Name is required']"
                  class="mb-2"
                ></v-text-field>

                <v-text-field
                  v-model="contactForm.email"
                  label="Email Address"
                  outlined
                  dense
                  :rules="[
                    v => !!v || 'Email is required',
                    v => /.+@.+\..+/.test(v) || 'Email must be valid'
                  ]"
                  class="mb-2"
                ></v-text-field>

                <v-text-field
                  v-model="contactForm.phone"
                  label="Phone Number"
                  outlined
                  dense
                  :rules="[v => !!v || 'Phone is required']"
                  class="mb-4"
                ></v-text-field>

                <!-- Submit Button -->
                <v-btn
                  block
                  x-large
                  color="#3A7D7C"
                  dark
                  class="connect-btn mb-3"
                  @click="connectWithAgentForHome"
                  :loading="connectingAgent"
                  :disabled="!contactFormValid"
                >
                  <i class="material-icons-round mr-2">send</i>
                  Submit & Connect
                </v-btn>

                <!-- Back Button -->
                <v-btn
                  block
                  large
                  outlined
                  color="#3A7D7C"
                  @click="showContactForm = false"
                  :disabled="connectingAgent"
                >
                  <i class="material-icons-round mr-2">arrow_back</i>
                  Back
                </v-btn>
              </v-form>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Floating Pre-Approval Button (when scrolled) -->
      <v-fab-transition>
        <v-btn
          v-if="showFloatingButton && !preApprovalAmount"
          fab
          fixed
          bottom
          right
          color="#3A7D7C"
          dark
          large
          class="floating-approval-btn"
          @click="openPreApprovalModal"
        >
          <i class="material-icons-round">calculate</i>
        </v-btn>
      </v-fab-transition>

      <!-- Home Listings Grid -->
      <v-container class="mt-6">
        <v-row>
          <v-col
            v-for="home in filteredHomes"
            :key="home.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              class="home-card"
              elevation="2"
              @click="preApprovalAmount && home.price <= preApprovalAmount ? viewHomeDetails(home) : null"
              :class="{
                'qualified-home': preApprovalAmount && home.price <= preApprovalAmount,
                'not-qualified-home': preApprovalAmount && home.price > preApprovalAmount
              }"
            >
              <!-- Home Image -->
              <div class="home-image-container">
                <v-img
                  :src="home.image"
                  height="220"
                  class="home-image"
                  cover
                >
                  <div class="home-status-badge" :class="home.status">
                    {{ home.status }}
                  </div>
                  <!-- Qualified Badge -->
                  <div
                    v-if="preApprovalAmount && home.price <= preApprovalAmount"
                    class="qualified-badge"
                  >
                    <i class="material-icons-round">check_circle</i>
                    <span>You Qualify</span>
                  </div>
                  <!-- Not Qualified Overlay -->
                  <div
                    v-if="preApprovalAmount && home.price > preApprovalAmount"
                    class="not-qualified-overlay"
                  >
                    <div class="overlay-content">
                      <i class="material-icons-round">lock</i>
                      <span>Above Your Budget</span>
                    </div>
                  </div>
                </v-img>
              </div>

              <!-- Home Details -->
              <v-card-text class="home-details">
                <div class="home-price">${{ formatPrice(home.price) }}</div>
                <div class="home-address">{{ home.address }}</div>
                <div class="home-location">{{ home.city }}, {{ home.state }} {{ home.zip }}</div>

                <!-- Home Features -->
                <div class="home-features mt-3">
                  <div class="feature-item">
                    <i class="material-icons-round">bed</i>
                    <span>{{ home.bedrooms }} Beds</span>
                  </div>
                  <div class="feature-item">
                    <i class="material-icons-round">bathtub</i>
                    <span>{{ home.bathrooms }} Baths</span>
                  </div>
                  <div class="feature-item">
                    <i class="material-icons-round">square_foot</i>
                    <span>{{ formatNumber(home.sqft) }} sqft</span>
                  </div>
                </div>

                <!-- Home Type -->
                <div class="home-type mt-2">
                  <v-chip small color="#3A7D7C" text-color="white">
                    {{ home.type }}
                  </v-chip>
                </div>
              </v-card-text>

              <!-- Action Buttons -->
              <v-card-actions class="home-actions">
                <v-btn
                  text
                  color="#3A7D7C"
                  small
                  @click.stop="contactAgent(home)"
                  :disabled="preApprovalAmount && home.price > preApprovalAmount"
                >
                  <i class="material-icons-round mr-1">phone</i>
                  Contact
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="#3A7D7C"
                  small
                  @click.stop="scheduleViewing(home)"
                  :disabled="preApprovalAmount && home.price > preApprovalAmount"
                >
                  <i class="material-icons-round mr-1">event</i>
                  Schedule
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- No Results -->
        <v-row v-if="filteredHomes.length === 0" class="mt-6">
          <v-col cols="12" class="text-center">
            <i class="material-icons-round no-results-icon">home_work</i>
            <h3 class="mt-4">No homes found</h3>
            <p class="text-muted">Try adjusting your search criteria</p>
          </v-col>
        </v-row>
      </v-container>

      <!-- Footer -->
      <div class="inventory-footer">
        <v-container>
          <v-row align="center" justify="center">
            <v-col cols="12" class="text-center">
              <h3 class="footer-brand">HomeSyncX</h3>
              <p class="footer-tagline">Your Journey Home Starts Here</p>
              <div class="footer-verified mt-2">
                <i class="material-icons-round">verified</i>
                <span>Verified Partner</span>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-main>
  </v-app>
</template>

<script>

export default {
  name: 'HomeInventory',
  data() {
    return {
      loader: false,
      searchTerm: '',
      sortBy: 'price-low',
      sortOptions: [
        { text: 'Price: Low to High', value: 'price-low' },
        { text: 'Price: High to Low', value: 'price-high' },
        { text: 'Bedrooms: Most to Least', value: 'bedrooms-high' },
        { text: 'Square Feet: Largest First', value: 'sqft-high' },
        { text: 'Newest Listings', value: 'newest' }
      ],
      // Pre-Approval
      preApprovalDialog: false,
      showFloatingButton: false,
      calculating: false,
      preApprovalAmount: null,
      showDebtField: false,
      connectingAgent: false,
      downPaymentType: 'percentage', // 'percentage' or 'fixed'
      preApprovalForm: {
        creditScore: null,
        monthlyIncome: null,
        monthlyDebt: 0,
        downPaymentPercent: 20, // Default 20%
        downPaymentFixed: null
      },
      creditScoreOptions: [
        { text: 'Excellent (740+)', value: 740 },
        { text: 'Good (670-739)', value: 670 },
        { text: 'Fair (580-669)', value: 580 },
        { text: 'Poor (Below 580)', value: 500 }
      ],
      agentUserId: null,
      base_url: process.env.VUE_APP_API_URL,

      // Agent Connection Dialog
      agentConnectionDialog: false,
      selectedHome: null,
      showContactForm: false,
      contactFormValid: false,
      contactForm: {
        name: '',
        email: '',
        phone: ''
      },
      connectingAgent: false,
      homes: [
        {
          id: 1,
          address: '123 Maple Street',
          city: 'Dallas',
          state: 'TX',
          zip: '75201',
          price: 450000,
          bedrooms: 4,
          bathrooms: 3,
          sqft: 2500,
          type: 'Single Family',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'
        },
        {
          id: 2,
          address: '456 Oak Avenue',
          city: 'Austin',
          state: 'TX',
          zip: '78701',
          price: 625000,
          bedrooms: 5,
          bathrooms: 4,
          sqft: 3200,
          type: 'Single Family',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800'
        },
        {
          id: 3,
          address: '789 Pine Road',
          city: 'Houston',
          state: 'TX',
          zip: '77001',
          price: 385000,
          bedrooms: 3,
          bathrooms: 2.5,
          sqft: 2100,
          type: 'Townhouse',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800'
        },
        {
          id: 4,
          address: '321 Elm Boulevard',
          city: 'San Antonio',
          state: 'TX',
          zip: '78201',
          price: 295000,
          bedrooms: 3,
          bathrooms: 2,
          sqft: 1800,
          type: 'Condo',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'
        },
        {
          id: 5,
          address: '654 Birch Lane',
          city: 'Fort Worth',
          state: 'TX',
          zip: '76101',
          price: 520000,
          bedrooms: 4,
          bathrooms: 3.5,
          sqft: 2800,
          type: 'Single Family',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800'
        },
        {
          id: 6,
          address: '987 Cedar Court',
          city: 'Plano',
          state: 'TX',
          zip: '75023',
          price: 475000,
          bedrooms: 4,
          bathrooms: 3,
          sqft: 2600,
          type: 'Single Family',
          status: 'Pending',
          image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800'
        },
        {
          id: 7,
          address: '147 Willow Drive',
          city: 'Arlington',
          state: 'TX',
          zip: '76001',
          price: 340000,
          bedrooms: 3,
          bathrooms: 2,
          sqft: 1950,
          type: 'Townhouse',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800'
        },
        {
          id: 8,
          address: '258 Spruce Street',
          city: 'Irving',
          state: 'TX',
          zip: '75038',
          price: 410000,
          bedrooms: 4,
          bathrooms: 2.5,
          sqft: 2300,
          type: 'Single Family',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=800'
        },
        {
          id: 9,
          address: '369 Redwood Place',
          city: 'Frisco',
          state: 'TX',
          zip: '75034',
          price: 550000,
          bedrooms: 5,
          bathrooms: 4,
          sqft: 3100,
          type: 'Single Family',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800'
        },
        {
          id: 10,
          address: '741 Magnolia Way',
          city: 'McKinney',
          state: 'TX',
          zip: '75069',
          price: 395000,
          bedrooms: 3,
          bathrooms: 2.5,
          sqft: 2200,
          type: 'Single Family',
          status: 'For Sale',
          image: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800'
        }
      ]
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.getAgentFromDomain();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  computed: {
    isFormValid() {
      const basicValid = this.preApprovalForm.creditScore &&
                        this.preApprovalForm.monthlyIncome &&
                        parseFloat(this.preApprovalForm.monthlyIncome) > 0;

      // Validate down payment based on type
      if (this.downPaymentType === 'percentage') {
        const percent = parseFloat(this.preApprovalForm.downPaymentPercent);
        return basicValid && percent > 0 && percent <= 100;
      } else {
        const fixed = parseFloat(this.preApprovalForm.downPaymentFixed);
        return basicValid && fixed >= 0;
      }
    },
    qualifiedHomesCount() {
      if (!this.preApprovalAmount) return 0;
      return this.homes.filter(home => home.price <= this.preApprovalAmount).length;
    },
    filteredHomes() {
      let filtered = [...this.homes];

      // Search filter
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        filtered = filtered.filter(home =>
          home.address.toLowerCase().includes(term) ||
          home.city.toLowerCase().includes(term) ||
          home.state.toLowerCase().includes(term) ||
          home.type.toLowerCase().includes(term)
        );
      }

      // Sort
      switch (this.sortBy) {
        case 'price-low':
          filtered.sort((a, b) => a.price - b.price);
          break;
        case 'price-high':
          filtered.sort((a, b) => b.price - a.price);
          break;
        case 'bedrooms-high':
          filtered.sort((a, b) => b.bedrooms - a.bedrooms);
          break;
        case 'sqft-high':
          filtered.sort((a, b) => b.sqft - a.sqft);
          break;
        case 'newest':
          filtered.sort((a, b) => b.id - a.id);
          break;
      }

      return filtered;
    }
  },
  methods: {
    async getAgentFromDomain() {
      try {
        // Get domain from URL (e.g., johndoe.homesyncx.com)
        const hostname = window.location.hostname;
        const subdomain = hostname.split('.')[0];

        // Call API to get agent user_id from domain
        const response = await axios.post('/api/get-user-by-domain', {
          domain: subdomain
        });

        if (response.data.flag === 1 && response.data.user) {
          this.agentUserId = response.data.user.user_id;
        }
      } catch (error) {
        console.error('Error getting agent from domain:', error);
      }
    },
    handleScroll() {
      // Show floating button after scrolling 300px
      this.showFloatingButton = window.scrollY > 300;
    },
    openPreApprovalModal() {
      this.preApprovalDialog = true;
    },
    closePreApprovalModal() {
      this.preApprovalDialog = false;
    },
    calculatePreApproval() {
      this.calculating = true;

      // Simulate calculation delay
      setTimeout(() => {
        const income = parseFloat(this.preApprovalForm.monthlyIncome);
        const debt = this.showDebtField ? parseFloat(this.preApprovalForm.monthlyDebt || 0) : 0;
        const creditScore = this.preApprovalForm.creditScore;

        // Calculate debt-to-income ratio
        const dti = debt / income;

        // Determine interest rate based on credit score
        let interestRate;
        if (creditScore >= 740) {
          interestRate = 0.065; // 6.5%
        } else if (creditScore >= 670) {
          interestRate = 0.07; // 7%
        } else if (creditScore >= 580) {
          interestRate = 0.075; // 7.5%
        } else {
          interestRate = 0.085; // 8.5%
        }

        // Calculate maximum monthly payment (28% of gross income minus debt)
        const maxMonthlyPayment = (income * 0.28) - debt;

        // Calculate loan amount using mortgage formula
        // P = (M * (1 - (1 + r)^-n)) / r
        // Where: P = loan amount, M = monthly payment, r = monthly interest rate, n = number of payments (360 for 30 years)
        const monthlyRate = interestRate / 12;
        const numPayments = 360; // 30 years

        const loanAmount = (maxMonthlyPayment * (1 - Math.pow(1 + monthlyRate, -numPayments))) / monthlyRate;

        // Calculate home price based on down payment type
        let homePrice;
        if (this.downPaymentType === 'percentage') {
          // Calculate based on percentage
          const downPaymentPercent = parseFloat(this.preApprovalForm.downPaymentPercent || 20);
          const downPaymentDecimal = downPaymentPercent / 100;
          homePrice = loanAmount / (1 - downPaymentDecimal);
        } else {
          // Calculate based on fixed amount
          const downPaymentFixed = parseFloat(this.preApprovalForm.downPaymentFixed || 0);
          homePrice = loanAmount + downPaymentFixed;
        }

        // Round to nearest $5,000
        this.preApprovalAmount = Math.floor(homePrice / 5000) * 5000;

        this.calculating = false;
      }, 1500);
    },
    applyPreApprovalFilter() {
      // Close modal and scroll to listings
      this.preApprovalDialog = false;

      // Scroll to listings section
      setTimeout(() => {
        const listingsSection = document.querySelector('.home-card');
        if (listingsSection) {
          listingsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    },
    async connectWithAgent() {
      if (!this.agentUserId) {
        this.$toast.error('Unable to connect with agent. Please try again.', {
          position: 'top-right'
        });
        return;
      }

      this.connectingAgent = true;

      try {
        // Get current user info (if logged in)
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

        // Prepare pre-approval data
        const preApprovalData = {
          agent_user_id: this.agentUserId,
          visitor_name: currentUser.first_name || 'Visitor',
          visitor_email: currentUser.email || null,
          visitor_phone: currentUser.phone_m || null,
          pre_approval_amount: this.preApprovalAmount,
          credit_score: this.preApprovalForm.creditScore,
          monthly_income: this.preApprovalForm.monthlyIncome,
          monthly_debt: this.showDebtField ? this.preApprovalForm.monthlyDebt : 0,
          qualified_homes_count: this.qualifiedHomesCount,
          platform: 'homesync',
          created_at: new Date().toISOString()
        };

        // Send to API
        const response = await axios.post('/api/submit-pre-approval-lead', preApprovalData);

        if (response.data.flag === 1) {
          this.connectingAgent = false;

          // Show success message
          this.$toast.success('Successfully connected with your agent! They will contact you shortly.', {
            position: 'top-right',
            duration: 5000
          });

          // Close modal and show listings
          this.applyPreApprovalFilter();
        } else {
          throw new Error(response.data.message || 'Failed to connect with agent');
        }
      } catch (error) {
        console.error('Error connecting with agent:', error);
        this.connectingAgent = false;

        this.$toast.error('Failed to connect with agent. Please try again.', {
          position: 'top-right'
        });
      }
    },
    async connectWithAgentForHome() {
      // Validate form
      if (!this.$refs.contactForm.validate()) {
        this.$toast.error('Please fill in all required fields.', {
          position: 'top-right'
        });
        return;
      }

      if (!this.agentUserId) {
        this.$toast.error('Unable to connect with agent. Please try again.', {
          position: 'top-right'
        });
        return;
      }

      if (!this.selectedHome) {
        this.$toast.error('No home selected.', {
          position: 'top-right'
        });
        return;
      }

      this.connectingAgent = true;

      try {
        // Prepare lead data with contact form info
        const leadData = {
          agent_user_id: this.agentUserId,
          visitor_name: this.contactForm.name,
          visitor_email: this.contactForm.email,
          visitor_phone: this.contactForm.phone,
          pre_approval_amount: this.preApprovalAmount || null,
          credit_score: this.preApprovalForm.creditScore || null,
          monthly_income: this.preApprovalForm.monthlyIncome || null,
          monthly_debt: this.showDebtField ? this.preApprovalForm.monthlyDebt : 0,
          down_payment_type: this.downPaymentType,
          down_payment_percent: this.downPaymentType === 'percentage' ? this.preApprovalForm.downPaymentPercent : null,
          down_payment_fixed: this.downPaymentType === 'fixed' ? this.preApprovalForm.downPaymentFixed : null,
          qualified_homes_count: this.qualifiedHomesCount || 0,
          interested_home: {
            address: this.selectedHome.address,
            price: this.selectedHome.price,
            beds: this.selectedHome.beds,
            baths: this.selectedHome.baths,
            sqft: this.selectedHome.sqft,
            type: this.selectedHome.type,
            status: this.selectedHome.status
          },
          platform: 'homesync',
          created_at: new Date().toISOString()
        };

        // Send to API
        const response = await axios.post('/api/submit-pre-approval-lead', leadData);

        if (response.data.flag === 1) {
          this.connectingAgent = false;

          // Show success message
          this.$toast.success('Successfully connected with your agent! They will contact you shortly about this property.', {
            position: 'top-right',
            duration: 5000
          });

          // Reset and close modal
          this.closeAgentDialog();
        } else {
          throw new Error(response.data.message || 'Failed to connect with agent');
        }
      } catch (error) {
        console.error('Error connecting with agent:', error);
        this.connectingAgent = false;

        this.$toast.error('Failed to connect with agent. Please try again.', {
          position: 'top-right'
        });
      }
    },
    closeAgentDialog() {
      this.agentConnectionDialog = false;
      this.selectedHome = null;
      this.showContactForm = false;
      this.contactForm = {
        name: '',
        email: '',
        phone: ''
      };
      if (this.$refs.contactForm) {
        this.$refs.contactForm.reset();
      }
    },
    formatPrice(price) {
      return price.toLocaleString();
    },
    formatNumber(num) {
      return num.toLocaleString();
    },
    viewHomeDetails(home) {
      // Store selected home and show agent connection dialog
      this.selectedHome = home;
      this.prefillContactForm();
      this.agentConnectionDialog = true;
    },
    contactAgent(home) {
      // Store selected home and show agent connection dialog
      this.selectedHome = home;
      this.prefillContactForm();
      this.agentConnectionDialog = true;
    },
    scheduleViewing(home) {
      // Store selected home and show agent connection dialog
      this.selectedHome = home;
      this.prefillContactForm();
      this.agentConnectionDialog = true;
    },
    prefillContactForm() {
      // Pre-fill form with logged-in user data if available
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser.first_name || currentUser.email || currentUser.phone_m) {
        this.contactForm.name = currentUser.first_name ?
          `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : '';
        this.contactForm.email = currentUser.email || '';
        this.contactForm.phone = currentUser.phone_m || '';
      }
    },
    shareLink() {
      // Get current HomeSyncX URL
      const url = window.location.origin + window.location.pathname;

      if (navigator.share) {
        navigator
          .share({
            title: "HomeSyncX - Find Your Dream Home",
            text: "Check out these amazing homes available now!",
            url: url
          })
          .then(() => console.log("Thanks for sharing!"))
          .catch(error => console.log("Share error:", error));
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(url).then(() => {
          this.$toast.success('Link copied to clipboard!', {
            position: 'top-right'
          });
        }).catch(() => {
          this.$toast.error('Failed to copy link', {
            position: 'top-right'
          });
        });
      }
    }
  }
};
</script>

<style scoped>
.home-inventory-app {
  background: #f5f7fa;
}

.home-inventory-main {
  min-height: 100vh;
  padding-bottom: 80px;
}

/* Header Section */
.inventory-header {
  background: linear-gradient(135deg, #2C3E50 0%, #3A7D7C 100%);
  color: white;
  padding: 40px 0 30px;
}

.header-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
}

.header-subtitle {
  font-size: 18px;
  opacity: 0.9;
  margin: 0;
}

/* Pre-Approval Alert */
.pre-approval-alert {
  border-radius: 12px !important;
  border: none !important;
}

.pre-approval-btn {
  color: #3A7D7C !important;
  font-weight: 600;
  text-transform: none;
  font-size: 16px;
}

/* Pre-Approval Modal */
.pre-approval-modal {
  border-radius: 16px !important;
}

.modal-header {
  background: linear-gradient(135deg, #2C3E50 0%, #3A7D7C 100%);
  color: white !important;
  font-size: 24px;
  font-weight: 700;
  padding: 20px 24px;
}

.modal-subtitle {
  font-size: 16px;
  color: #6B8E9E;
  text-align: center;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2C3E50;
  margin-bottom: 8px;
}

.calculate-btn {
  text-transform: none;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
}

.results-section {
  animation: slideIn 0.5s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.results-alert {
  border-radius: 12px !important;
}

.approval-amount {
  font-size: 48px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  color: white;
}

/* Agent Connection Card */
.agent-connect-card {
  border: 2px solid #3A7D7C !important;
  border-radius: 12px !important;
  background: #f8fffe !important;
}

.connect-agent-btn {
  text-transform: none;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
}

/* Agent Connection Modal */
.agent-connection-modal .modal-header {
  background: linear-gradient(135deg, #3A7D7C 0%, #4A9B8E 100%);
  color: white;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.agent-connection-modal .header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
}

.agent-connection-modal .header-content i {
  font-size: 28px;
}

.agent-connection-modal .modal-header .v-btn {
  color: white;
}

.selected-home-info h3 {
  color: #2C3E50;
  font-size: 20px;
  font-weight: 600;
}

.selected-home-info .home-details {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: #6B8E9E;
  font-size: 14px;
}

.selected-home-info .price {
  color: #3A7D7C;
  font-size: 24px;
  font-weight: 700;
}

.selected-home-info .separator {
  color: #ccc;
}

.connection-message h3 {
  color: #2C3E50;
  font-size: 22px;
  font-weight: 600;
}

.pre-approval-badge {
  display: inline-flex;
  align-items: center;
  background: #E8F5E9;
  color: #2E7D32;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
}

.pre-approval-badge i {
  font-size: 24px;
}

.connect-btn {
  text-transform: none;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  padding: 14px 0 !important;
}

/* Floating Button */
.floating-approval-btn {
  bottom: 24px !important;
  right: 24px !important;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(58, 125, 124, 0.4) !important;
}

.search-input {
  background: white;
  border-radius: 8px;
}

.sort-select {
  background: white;
  border-radius: 8px;
}

/* Home Card */
.home-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.home-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
}

/* Qualified Home Styling */
.qualified-home {
  border: 3px solid #4CAF50;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3) !important;
}

.qualified-home:hover {
  box-shadow: 0 8px 24px rgba(76, 175, 80, 0.4) !important;
}

.not-qualified-home {
  opacity: 0.5;
  filter: grayscale(50%);
  pointer-events: none;
  cursor: not-allowed;
}

.not-qualified-home:hover {
  opacity: 0.5;
  transform: none;
}

.qualified-badge {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: #4CAF50;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

.qualified-badge i {
  font-size: 18px;
}

.not-qualified-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}

.overlay-content {
  background: rgba(255, 255, 255, 0.95);
  color: #2C3E50;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.overlay-content i {
  font-size: 20px;
  color: #FF5252;
}

.home-image-container {
  position: relative;
  overflow: hidden;
}

.home-image {
  transition: transform 0.3s ease;
}

.home-card:hover .home-image {
  transform: scale(1.05);
}

.home-status-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #3A7D7C;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.home-status-badge.Pending {
  background: #FF9800;
}

/* Home Details */
.home-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.home-price {
  font-size: 28px;
  font-weight: 700;
  color: #3A7D7C;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
}

.home-address {
  font-size: 16px;
  font-weight: 600;
  color: #2C3E50;
  margin-bottom: 4px;
}

.home-location {
  font-size: 14px;
  color: #6B8E9E;
  margin-bottom: 12px;
}

/* Home Features */
.home-features {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #2C3E50;
}

.feature-item i {
  font-size: 18px;
  color: #3A7D7C;
}

.home-type {
  margin-top: auto;
}

/* Home Actions */
.home-actions {
  border-top: 1px solid #e0e0e0;
  padding: 12px 16px;
}

/* Footer */
.inventory-footer {
  background: #1A2332;
  color: white;
  padding: 40px 0;
  margin-top: 60px;
}

.footer-brand {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
}

.footer-tagline {
  font-size: 16px;
  opacity: 0.8;
  margin: 0;
}

.footer-verified {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  opacity: 0.9;
}

.footer-verified i {
  font-size: 20px;
  color: #3A7D7C;
}

/* No Results */
.no-results-icon {
  font-size: 80px;
  color: #ccc;
}

.text-muted {
  color: #6B8E9E;
}

/* Responsive */
@media (max-width: 768px) {
  .header-title {
    font-size: 28px;
  }

  .header-subtitle {
    font-size: 16px;
  }

  .home-price {
    font-size: 24px;
  }

  .home-features {
    gap: 12px;
  }
}
</style>

